import { useEffect, useState } from "react";
import { Form as AntForm } from "antd";
import {
  Button,
  Form,
  FormItem,
  Input,
  TextArea,
  useForm,
  Message,
  Select,
} from "@/frontend/components/common";
import { matchTeamService, disciplineService, subCategoryService } from "@/frontend/services";
import { useNavigate } from "react-router-dom";
import type {
  CreateMatchTeamRequestDTO,
  UpdateMatchTeamRequestDTO,
  DisciplineResponseDTO,
  SubCategoryResponseDTO,
} from "@/shared/dto";

interface MatchTeamFormValues {
  disciplineId: string;
  subCategoryIds?: string[];
  name: string;
  description?: string;
  imageUrl?: string;
}

interface Props {
  matchTeamId?: string;
  initialValues?: Partial<MatchTeamFormValues>;
}

export function MatchTeamForm({ matchTeamId, initialValues }: Props) {
  const navigate = useNavigate();
  const [form] = useForm();
  const isEditMode = !!matchTeamId;

  const [disciplines, setDisciplines] = useState<DisciplineResponseDTO[]>([]);
  const [subCategories, setSubCategories] = useState<SubCategoryResponseDTO[]>([]);
  const [loadingDisciplines, setLoadingDisciplines] = useState(false);
  const [loadingSubCategories, setLoadingSubCategories] = useState(false);
  const [logoError, setLogoError] = useState(false);

  const imageUrl = AntForm.useWatch("imageUrl", form);

  useEffect(() => {
    async function loadDisciplines() {
      setLoadingDisciplines(true);
      try {
        const res = await disciplineService.getPaginate({ page: 1, pageSize: 100 });
        if (res.success && res.data) setDisciplines(res.data.data);
      } finally {
        setLoadingDisciplines(false);
      }
    }
    loadDisciplines();
  }, []);

  async function loadSubCategories(disciplineId: string) {
    setLoadingSubCategories(true);
    setSubCategories([]);
    try {
      const res = await subCategoryService.getPaginate({ page: 1, pageSize: 100, disciplineId });
      if (res.success && res.data) setSubCategories(res.data.data);
    } finally {
      setLoadingSubCategories(false);
    }
  }

  useEffect(() => {
    if (initialValues) {
      form.setFieldsValue(initialValues);
      if (initialValues.disciplineId) {
        loadSubCategories(initialValues.disciplineId);
      }
    }
  }, [initialValues, form]);

  useEffect(() => {
    setLogoError(false);
  }, [imageUrl]);

  function handleDisciplineChange(disciplineId: string) {
    form.setFieldValue("subCategoryIds", []);
    loadSubCategories(disciplineId);
  }

  const handleSubmit = async (values: MatchTeamFormValues) => {
    try {
      if (isEditMode && matchTeamId) {
        const updateData: UpdateMatchTeamRequestDTO = {
          disciplineId: values.disciplineId,
          subCategoryIds: values.subCategoryIds ?? [],
          name: values.name,
          description: values.description || undefined,
          imageUrl: values.imageUrl || null,
        };
        const res = await matchTeamService.update(matchTeamId, updateData);
        if (res.success) {
          Message.success({ content: "Equipo actualizado correctamente" });
          navigate("/match-teams");
        } else {
          Message.error({ content: res.error || "Error al actualizar" });
        }
      } else {
        const createData: CreateMatchTeamRequestDTO = {
          disciplineId: values.disciplineId,
          subCategoryIds: values.subCategoryIds ?? [],
          name: values.name,
          description: values.description || undefined,
          imageUrl: values.imageUrl || undefined,
        };
        const res = await matchTeamService.create(createData);
        if (res.success) {
          Message.success({ content: "Equipo creado correctamente" });
          navigate("/match-teams");
        } else {
          Message.error({ content: res.error || "Error al crear" });
        }
      }
    } catch (err) {
      Message.error({ content: err instanceof Error ? err.message : "Error inesperado" });
    }
  };

  return (
    <Form form={form} onFinish={handleSubmit} layout="vertical" className="w-full">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <FormItem
          name="disciplineId"
          label="Deporte / Disciplina"
          rules={[{ required: true, message: "La disciplina es requerida" }]}
        >
          <Select
            placeholder="Seleccionar deporte"
            loading={loadingDisciplines}
            size="large"
            showSearch
            onChange={handleDisciplineChange}
            options={disciplines.map((d) => ({ label: d.name, value: d.id }))}
          />
        </FormItem>

        <FormItem name="subCategoryIds" label="Categorías">
          <Select
            mode="multiple"
            placeholder="Seleccionar una o más categorías (opcional)"
            loading={loadingSubCategories}
            size="large"
            allowClear
            options={subCategories.map((s) => ({ label: s.category, value: s.id }))}
          />
        </FormItem>

        <FormItem
          name="name"
          label="Nombre del equipo"
          rules={[{ required: true, message: "El nombre es requerido" }]}
        >
          <Input placeholder="Ej: FC Barcelona, Club Atlético River Plate" size="large" />
        </FormItem>

        <FormItem label="URL del escudo / logo">
          <div className="flex items-center gap-3">
            {imageUrl && !logoError && (
              <img
                src={imageUrl}
                alt="Escudo del equipo"
                className="w-12 h-12 object-contain rounded border border-gray-200 shrink-0 p-1"
                onError={() => setLogoError(true)}
              />
            )}
            <FormItem name="imageUrl" noStyle>
              <Input placeholder="Se obtiene automáticamente si se deja vacío" size="large" />
            </FormItem>
          </div>
        </FormItem>

        <FormItem name="description" label="Descripción" className="md:col-span-2">
          <TextArea placeholder="Descripción breve del equipo (opcional)" rows={3} size="large" />
        </FormItem>
      </div>

      <div className="flex justify-end gap-4 mt-6">
        <Button type="primary" htmlType="submit" size="large">
          {isEditMode ? "Actualizar Equipo" : "Crear Equipo"}
        </Button>
        <Button size="large" onClick={() => navigate("/match-teams")}>
          Cancelar
        </Button>
      </div>
    </Form>
  );
}
